
import { blurActiveElement } from '@/core/directives/dialogAutoFocus';
import { defineComponent } from 'vue';

export default defineComponent({
  name: 'BaseModal',

  props: {
    isDisplay: {
      type: Boolean,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    width: {
      type: String,
      default: '50%',
    },
    size: {
      type: String,
      default: '',
      validator: (value: string) =>
        ['', 'sm', 'lg', 'xl', 'xxl'].includes(value),
    },
    /** When true, clicking the dimmed area outside the dialog dismisses it. Default: false (static backdrop). */
    closeOnBackdropClick: {
      type: Boolean,
      default: false,
    },
    closeOnEscape: {
      type: Boolean,
      default: true,
    },
    showFooter: {
      type: Boolean,
      default: true,
    },
    isCloseDisabled: {
      type: Boolean,
      default: false,
    },
    backdropZIndex: {
      type: Number,
      default: 1040,
    },
    modalZIndex: {
      type: Number,
      default: 1050,
    },
  },

  emits: ['close'],

  data() {
    return {
      isDialogDisplay: false,
    };
  },

  computed: {
    modalSizeClass() {
      return this.size ? `modal-${this.size}` : '';
    },
    modalStyle() {
      if (this.size) {
        return {};
      }

      return { maxWidth: this.width };
    },
  },

  watch: {
    isDisplay: {
      handler(newValue: boolean) {
        if (newValue) {
          blurActiveElement();
        }

        this.isDialogDisplay = newValue;
        this.toggleBodyScroll();
      },
      immediate: true,
    },
  },

  mounted() {
    document.addEventListener('keydown', this.onEscapeKey);
  },

  beforeUnmount() {
    document.removeEventListener('keydown', this.onEscapeKey);
    this.restoreBodyScroll();
  },

  methods: {
    onClose() {
      if (this.isCloseDisabled) {
        return;
      }

      this.$emit('close');
    },

    onBackdropClick() {
      if (this.closeOnBackdropClick) {
        this.onClose();
      }
    },

    onEscapeKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && this.isDialogDisplay && this.closeOnEscape) {
        this.onClose();
      }
    },

    toggleBodyScroll() {
      if (this.isDialogDisplay) {
        this.preventBodyScroll();
      } else {
        this.restoreBodyScroll();
      }
    },

    preventBodyScroll() {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${this.getScrollbarWidth()}px`;
    },

    restoreBodyScroll() {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    },

    getScrollbarWidth() {
      const outer = document.createElement('div');
      outer.style.visibility = 'hidden';
      outer.style.overflow = 'scroll';
      document.body.appendChild(outer);

      const inner = document.createElement('div');
      outer.appendChild(inner);

      const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
      outer.parentNode?.removeChild(outer);

      return scrollbarWidth;
    },
  },
});
